import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { format } from "date-fns";
import api, { apiError } from "../lib/api";
import { SERVICES } from "../data/content";
import { useSiteContent } from "../context/SiteContentContext";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, CalendarDays, Clock, IndianRupee, MapPin, ShieldCheck } from "lucide-react";
import { GroupedSlotGrid } from "./GroupedSlotGrid";

const STEPS = ["Select Service", "Choose Date & Time", "Review & Confirm", "Confirmation"];

export const BookingFlow = ({ type }) => {
  const navigate = useNavigate();
  const { clinic } = useSiteContent();
  const [params] = useSearchParams();
  const preselect = params.get("service");

  const [step, setStep] = useState(0);
  const [serviceId, setServiceId] = useState(preselect && SERVICES.find((s) => s.id === preselect) ? preselect : null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [pendingSlot, setPendingSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [availability, setAvailability] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!date) return;
    setSlot(null);
    setLoadingSlots(true);
    api
      .get("/availability", { params: { date: format(date, "yyyy-MM-dd"), type } })
      .then(({ data }) => setAvailability(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoadingSlots(false));
  }, [date, type]);

  const service = SERVICES.find((s) => s.id === serviceId);
  const pendingMeta = availability?.slots.find((s) => s.time === pendingSlot);
  const slotMeta = availability?.slots.find((s) => s.time === slot);

  const confirmBooking = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments", {
        type,
        service_id: serviceId,
        date: format(date, "yyyy-MM-dd"),
        slot,
        notes,
      });
      setResult(data);
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const disabledDays = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const max = new Date(today);
    max.setDate(max.getDate() + 60);
    if (d < today || d > max) return true;
    if (type === "online" && d.getTime() === today.getTime()) return true;
    if (type === "clinic" && d.getDay() === 0) return true;
    return false;
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="booking-flow">
      {/* Stepper */}
      <div className="flex items-center justify-between mb-10">
        {STEPS.map((label, i) => (
          <div key={label} className="flex-1 flex flex-col items-center relative">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold z-10 transition-colors duration-300 ${
                i < step
                  ? "bg-[#5E8B7E] text-white"
                  : i === step
                  ? "bg-[#2C557E] text-white"
                  : "bg-[#D9E4E1] text-slate-500"
              }`}
            >
              {i < step ? <CheckCircle2 className="w-5 h-5" /> : i + 1}
            </div>
            <span className="text-[11px] sm:text-xs mt-2 text-slate-600 text-center hidden sm:block">
              {label}
            </span>
            {i < STEPS.length - 1 && (
              <div className="absolute top-4.5 left-1/2 w-full h-0.5 bg-[#D9E4E1] -z-0" style={{ top: 18 }} />
            )}
          </div>
        ))}
      </div>

      {/* STEP 1 — Service */}
      {step === 0 && (
        <div className="fade-up">
          <h2 className="font-heading text-2xl md:text-3xl text-slate-800 mb-2">
            {type === "online" ? "What would you like to discuss?" : "Which service do you need?"}
          </h2>
          <p className="text-slate-500 mb-6 text-sm md:text-base">
            {type === "online"
              ? "Online consultations are 15-minute private video/phone sessions. Choose the area closest to your concern."
              : "Choose one of Dr. Prathusha's five core areas of care."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {SERVICES.map((s) => (
              <button
                key={s.id}
                data-testid={`booking-service-${s.id}`}
                onClick={() => setServiceId(s.id)}
                className={`text-left p-5 rounded-2xl border transition-all duration-300 ${
                  serviceId === s.id
                    ? "border-[#5E8B7E] bg-[#D9E4E1]/40 shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
                    : "border-[#E5EAE8] bg-white hover:border-[#5E8B7E]/50 hover:-translate-y-0.5"
                }`}
              >
                <div className="font-heading font-semibold text-slate-800">{s.name}</div>
                <div className="text-sm text-slate-500 mt-1">{s.tagline}</div>
              </button>
            ))}
          </div>
          <div className="mt-8 flex justify-end">
            <Button
              data-testid="booking-step1-next"
              disabled={!serviceId}
              onClick={() => setStep(1)}
              className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] px-8"
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* STEP 2 — Date & Slot */}
      {step === 1 && (
        <div className="fade-up">
          <h2 className="font-heading text-2xl md:text-3xl text-slate-800 mb-2">Choose a date & time</h2>
          <p className="text-slate-500 mb-6 text-sm md:text-base">
            {type === "online"
              ? `Online consultations: ${clinic.onlineHours} · 15-minute slots`
              : "Mon – Sat · Trust Health Care: 11:00 AM – 1:00 PM (Morning) · Care Hospitals: 5:00 – 7:00 PM (Evening)"}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl border border-[#E5EAE8] p-4 flex justify-center" data-testid="booking-calendar">
              <Calendar mode="single" selected={date} onSelect={setDate} disabled={disabledDays} />
            </div>
            <div>
              {!date && (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm border border-dashed border-[#D9E4E1] rounded-2xl p-8">
                  Select a date to see available slots
                </div>
              )}
              {date && loadingSlots && (
                <div className="h-full flex items-center justify-center">
                  <div className="w-6 h-6 border-4 border-[#5E8B7E] border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {date && !loadingSlots && availability && (
                <div>
                  {!availability.booking_enabled && (
                    <div data-testid="booking-disabled-notice" className="bg-[#E8DAB2]/50 border border-[#E8DAB2] rounded-xl p-4 text-sm text-slate-700 mb-4">
                      Appointments are temporarily paused by the clinic team for this mode. Please
                      check back soon or call {clinic.phone}.
                    </div>
                  )}
                  {availability.closed && (
                    <div className="bg-[#E8DAB2]/50 border border-[#E8DAB2] rounded-xl p-4 text-sm text-slate-700 mb-4">
                      {availability.closed_reason}
                    </div>
                  )}
                  <div data-testid="booking-slots">
                    <GroupedSlotGrid
                      slots={availability.slots}
                      selected={slot}
                      onSelect={setPendingSlot}
                      testIdPrefix="booking-slot"
                    />
                  </div>
                  <AlertDialog open={!!pendingSlot} onOpenChange={(open) => !open && setPendingSlot(null)}>
                    <AlertDialogContent className="rounded-2xl" data-testid="slot-confirm-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading">Confirm this slot?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <div className="text-sm text-slate-600 space-y-1.5 pt-1">
                            <div><b className="text-slate-800">{service?.name}</b></div>
                            <div>{date && format(date, "EEEE, dd MMM yyyy")} at <b className="text-slate-800">{pendingSlot}</b></div>
                            <div>
                              {type === "online"
                                ? "Online consultation (15 minutes)"
                                : pendingMeta?.location
                                ? `In-clinic visit · ${pendingMeta.location}`
                                : `In-clinic visit · ${clinic.address}`}
                            </div>
                            {type === "clinic" && pendingMeta?.location_address && (
                              <div className="text-xs text-slate-500">{pendingMeta.location_address}</div>
                            )}
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="slot-confirm-no" className="rounded-full">
                          Choose another
                        </AlertDialogCancel>
                        <AlertDialogAction
                          data-testid="slot-confirm-yes"
                          className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]"
                          onClick={() => {
                            setSlot(pendingSlot);
                            setPendingSlot(null);
                            setStep(2);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                        >
                          Yes, continue
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          </div>
          <div className="mt-8 flex justify-between">
            <Button variant="ghost" onClick={() => setStep(0)} className="rounded-full text-slate-500">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button
              data-testid="booking-step2-next"
              disabled={!date || !slot}
              onClick={() => setStep(2)}
              className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] px-8"
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* STEP 3 — Review */}
      {step === 2 && (
        <div className="fade-up">
          <h2 className="font-heading text-2xl md:text-3xl text-slate-800 mb-6">Review your request</h2>
          <div className="bg-white rounded-2xl border border-[#E5EAE8] p-6 space-y-4" data-testid="booking-review-card">
            <div className="flex flex-wrap gap-3 items-center">
              <Badge className="bg-[#D9E4E1] text-slate-800 hover:bg-[#D9E4E1] rounded-full">
                {type === "online" ? "Online Consultation (15 min)" : "In-Clinic Visit"}
              </Badge>
              <span className="font-heading font-semibold text-slate-800">{service?.name}</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-600">
              <span className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#5E8B7E]" />{date && format(date, "EEEE, dd MMM yyyy")}</span>
              <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#5E8B7E]" />{slot}</span>
            </div>
            {type === "clinic" && slotMeta?.location && (
              <div className="flex items-start gap-2 text-sm text-slate-600" data-testid="booking-review-location">
                <MapPin className="w-4 h-4 text-[#5E8B7E] mt-0.5 shrink-0" />
                <span><b className="text-slate-800">{slotMeta.location}</b> — {slotMeta.location_address}</span>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Anything you'd like the doctor to know? (optional)
              </label>
              <Textarea
                data-testid="booking-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Brief description of your concern, symptoms, or questions…"
                className="rounded-xl"
              />
            </div>
            <p className="text-xs text-slate-500 flex gap-2">
              <ShieldCheck className="w-4 h-4 shrink-0 text-[#5E8B7E]" />
              Your details are sent securely over an encrypted connection and are visible only to
              you and the doctor's team. Consultation fees will be shown on your confirmation screen.
            </p>
          </div>
          <div className="mt-8 flex justify-between">
            <Button variant="ghost" onClick={() => setStep(1)} className="rounded-full text-slate-500">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button
              data-testid="booking-confirm-btn"
              disabled={submitting}
              onClick={confirmBooking}
              className="rounded-full bg-[#2C557E] hover:bg-[#22436a] px-8"
            >
              {submitting ? "Reserving your slot…" : "Confirm Booking"}
            </Button>
          </div>
        </div>
      )}

      {/* STEP 4 — Confirmation with fee breakdown */}
      {step === 3 && result && (
        <div className="fade-up text-center" data-testid="booking-confirmation">
          <div className="w-16 h-16 rounded-full bg-[#D9E4E1] flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-9 h-9 text-[#5E8B7E]" />
          </div>
          <h2 className="font-heading text-2xl md:text-3xl text-slate-800">
            Thank you, {result.appointment.patient_name.split(" ")[0]} — your slot is reserved
          </h2>
          <p className="text-slate-500 mt-2 text-sm md:text-base max-w-xl mx-auto">
            Your request for <b>{result.appointment.service_name}</b> on{" "}
            <b>{result.appointment.date}</b> at <b>{result.appointment.slot}</b>
            {result.appointment.location && <> at <b>{result.appointment.location}</b></>} has been received
            and the slot is blocked for you. Our team will confirm it shortly — you'll receive a
            confirmation email.
          </p>

          <div className="bg-white rounded-2xl border border-[#E5EAE8] p-6 max-w-md mx-auto mt-8 text-left" data-testid="booking-fee-breakdown">
            <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2 mb-4">
              <IndianRupee className="w-4 h-4 text-[#5E8B7E]" /> Consultation Fee Breakdown (INR)
            </h3>
            <div className="space-y-2 text-sm text-slate-600">
              <div className="flex justify-between">
                <span>Consultation fee</span>
                <span>₹{result.fee_breakdown.consultation_fee}</span>
              </div>
              <div className="flex justify-between">
                <span>Registration fee</span>
                <span>₹{result.fee_breakdown.registration_fee}</span>
              </div>
              <div className="border-t border-dashed border-[#D9E4E1] pt-2 flex justify-between font-semibold text-slate-800">
                <span>Total payable</span>
                <span data-testid="booking-total-fee">₹{result.fee_breakdown.total}</span>
              </div>
            </div>
            {result.payment_note && (
              <p className="text-xs text-slate-500 mt-2 text-center" data-testid="booking-payment-note">
                ({result.payment_note})
              </p>
            )}
            <p className="text-xs text-slate-500 mt-4 bg-[#F4F7F6] rounded-xl p-3">
              {result.instructions}
            </p>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              data-testid="booking-goto-dashboard"
              onClick={() => navigate("/dashboard")}
              className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] px-8"
            >
              Go to My Dashboard
            </Button>
            <Button variant="outline" onClick={() => navigate("/")} className="rounded-full px-8">
              Back to Home
            </Button>
          </div>

          {type === "online" && (
            <div data-testid="booking-referral-note" className="mt-8 max-w-xl mx-auto bg-[#F4F7F6] border border-[#D9E4E1] rounded-xl p-4 text-xs text-slate-600 leading-relaxed">
              Dr. Prathusha works with a team of{" "}
              <Link to="/care-providers" data-testid="booking-care-providers-link" className="text-[#2C557E] font-medium hover:underline">
                care providers
              </Link>{" "}
              and other doctors. If you think you need an appointment with them, request for a referral after your consultation.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
